// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { produce } from 'immer';
import { type ClientHoldings } from '@shared/data-access/types/brokerage-types';

/**
 * Merges duplicate holdings by ticker/ISIN and currency.
 *
 * Consolidates holdings that share the same identifier (ticker or ISIN) and
 * currency into a single holding, aggregating their numeric values.
 *
 * @remarks
 * The merge logic works as follows:
 *
 * - Holdings are grouped by a composite key of `ticker:currency` or `isin:currency`
 * - For duplicate holdings, the following values are summed:
 *
 *   - `quantity` - Total number of units
 *   - `investment` - Total investment value
 *   - `acquisition_cost` - Total acquisition cost
 * - Holdings without both `ticker` and `isin` (null values) are kept separate and not
 *   merged, as they cannot be uniquely identified
 * - Other properties (name, price, asset_type, etc.) are taken from the first
 *   occurrence of each unique holding
 *
 * @example
 *
 * ```ts
 * const holdings = [
 *   { ticker: 'AAPL', currency: 'USD', quantity: 10, investment: 1500 },
 *   { ticker: 'AAPL', currency: 'USD', quantity: 5, investment: 750 },
 *   { ticker: 'GOOGL', currency: 'USD', quantity: 3, investment: 450 },
 * ];
 *
 * const merged = mergeClientHoldings(holdings);
 * // Result: [
 * //   { ticker: 'AAPL', currency: 'USD', quantity: 15, investment: 2250 },
 * //   { ticker: 'GOOGL', currency: 'USD', quantity: 3, investment: 450 }
 * // ]
 * ```
 *
 * @param holdings - Array of client holdings to merge
 * @returns Array of merged holdings with aggregated values for duplicates
 */
export const mergeClientHoldings = (
  holdings: ClientHoldings[],
): ClientHoldings[] => {
  const holdingsMap = new Map<string, ClientHoldings>();

  const mergedHoldings = produce(holdingsMap, draft => {
    holdings.forEach((holding, index) => {
      if (holding.isin === null && holding.ticker === null) {
        draft.set(index.toString(), holding);

        return;
      }

      const key = `${holding.ticker ?? holding.isin}:${holding.currency}`;

      if (draft.has(key)) {
        const currentHolding = draft.get(key)!;

        currentHolding.quantity += holding.quantity;

        currentHolding.investment += holding.investment;

        currentHolding.acquisition_cost =
          (currentHolding.acquisition_cost ?? 0) + (holding.acquisition_cost ?? 0);
      } else {
        draft.set(key, holding);
      }
    });
  });

  return Array.from(mergedHoldings.values());
};
